import React, { useEffect, useState } from "react";
import { Button, Paper, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Toaster } from "@/components";
import {
  getAssistedAffiliationListService,
  getDraftErrorMessage,
} from "@/services/assistedAffiliation";
import AssistedAffiliationFilterBar from "./components/AssistedAffiliationFilterBar";
import AssistedAffiliationManualRequest from "./components/AssistedAffiliationManualRequest";
import AssistedAffiliationTable from "./components/AssistedAffiliationTable";
import {
  EMPTY_FILTERS,
  LIST_LOAD_ERROR_MESSAGE,
  PAGE_SIZE_OPTIONS,
  REQUESTS_CHUNK_SIZE,
} from "./constants";
import type {
  AlertState,
  AssistedAffiliationFilters,
  AssistedAffiliationRequest,
  AssistedAffiliationRncScreenProps,
} from "./types";
import useStyle from "./styles";

const AssistedAffiliationRncScreen = ({
  readOnly = false,
}: AssistedAffiliationRncScreenProps) => {
  const classes = useStyle();
  const [filters, setFilters] =
    useState<AssistedAffiliationFilters>(EMPTY_FILTERS);
  const [isManualRequestOpen, setIsManualRequestOpen] = useState(false);
  const [requests, setRequests] = useState<AssistedAffiliationRequest[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    type: "info",
    text: "",
  });

  // La bandeja trae bloques bajo demanda en vez de todo el listado, y los acumula: la tabla
  // rebana localmente lo acumulado mientras el pie cuenta con el total del servidor.
  useEffect(() => {
    const fetchChunk = async () => {
      // El spinner es solo para la primera carga; los bloques siguientes llegan sin interrumpir
      // lo que el usuario ya está viendo.
      setLoading(currentChunk === 0);
      try {
        const response = await getAssistedAffiliationListService({
          pageNumber: currentChunk,
          pageSize: REQUESTS_CHUNK_SIZE,
        });
        // Ante una respuesta con otra forma, la bandeja queda vacía en lugar de romperse.
        const items = Array.isArray(response.items) ? response.items : [];
        setRequests((previous) => [...previous, ...items]);
        setTotalElements(response.total_elements ?? 0);
      } catch (error) {
        setAlert({
          show: true,
          type: "error",
          text: getDraftErrorMessage(error, LIST_LOAD_ERROR_MESSAGE),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchChunk();
  }, [currentChunk]);

  // Se pide el bloque siguiente solo si lo acumulado no alcanza para la página que sigue a la
  // que el usuario abre. Retroceder nunca lo dispara.
  const handleNextPage = (newPage = 0) => {
    if (requests.length >= totalElements) return;
    if (requests.length < (newPage + 2) * pageSize) {
      setCurrentChunk((chunk) => chunk + 1);
    }
  };

  if (isManualRequestOpen) {
    return (
      <Paper className={classes.paper} elevation={1}>
        <AssistedAffiliationManualRequest
          onBack={() => setIsManualRequestOpen(false)}
        />
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={1}>
      <div className={classes.header}>
        <Typography variant="h5" component="h2" className={classes.title}>
          Afiliación Asistida
        </Typography>
        <div className={classes.headerActions}>
          <Button
            variant="contained"
            className={classes.primaryButton}
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={() => setIsManualRequestOpen(true)}
          >
            Nueva solicitud
          </Button>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            startIcon={<GetAppIcon />}
          >
            Exportar
          </Button>
        </div>
      </div>

      <AssistedAffiliationFilterBar
        filters={filters}
        onFiltersChange={setFilters}
      />

      <AssistedAffiliationTable
        requests={requests}
        readOnly={readOnly}
        loading={loading}
        totalElements={totalElements}
        onNextPage={handleNextPage}
        onPageSizeChange={setPageSize}
      />

      <Toaster
        show={alert.show}
        type={alert.type}
        text={alert.text}
        onClose={() => setAlert((current) => ({ ...current, show: false }))}
      />
    </Paper>
  );
};

export default AssistedAffiliationRncScreen;
